import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography,
  makeStyles,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Box,
  Link,
  Paper,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoIcon from '@material-ui/icons/Info';
import CodeIcon from '@material-ui/icons/Code';
import HelpIcon from '@material-ui/icons/Help';
import LinkIcon from '@material-ui/icons/Link';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CloseIcon from '@material-ui/icons/Close';
import { useApi } from '@backstage/core-plugin-api';
import { yamlEditorApiRef, type KindSchema, type FieldInfo, type ValidationError } from '../../api';
import * as YAML from 'yaml';

// Panel size constants
export const PANEL_DEFAULT_WIDTH = 320;
export const PANEL_MIN_WIDTH = 260;
export const PANEL_MAX_WIDTH = 500;

const useStyles = makeStyles(theme => ({
  panelContainer: {
    position: 'relative',
    flexShrink: 0,
    height: '100%',
    display: 'flex',
  },
  resizeHandle: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    cursor: 'ew-resize',
    zIndex: 10,
    backgroundColor: 'transparent',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  resizeHandleActive: {
    backgroundColor: theme.palette.primary.main,
  },
  panel: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  header: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 36,
    '& .MuiTab-root': {
      minHeight: 36,
      fontSize: '0.75rem',
    },
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  kindDescription: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  fieldCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  fieldCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  fieldCardError: {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.type === 'dark' 
      ? 'rgba(244, 67, 54, 0.1)' 
      : 'rgba(244, 67, 54, 0.05)',
  },
  fieldCardValid: {
    borderColor: theme.palette.success.main,
    backgroundColor: theme.palette.type === 'dark' 
      ? 'rgba(76, 175, 80, 0.1)' 
      : 'rgba(76, 175, 80, 0.05)',
  },
  fieldHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexWrap: 'nowrap',
    minWidth: 0, // Allow flex items to shrink below content size
  },
  fieldName: {
    fontFamily: 'monospace',
    fontWeight: 600,
    fontSize: '0.85rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    flexShrink: 1,
  },
  requiredChip: {
    height: 18,
    fontSize: '0.65rem',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    flexShrink: 0,
  },
  optionalChip: {
    height: 18,
    fontSize: '0.65rem',
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
    flexShrink: 0,
  },
  inUseChip: {
    height: 18,
    fontSize: '0.65rem',
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    flexShrink: 0,
  },
  typeChip: {
    height: 18,
    fontSize: '0.65rem',
    fontFamily: 'monospace',
    flexShrink: 0,
  },
  fieldDescription: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.4,
  },
  fieldDetails: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
  validValuesList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  validValueChip: {
    height: 22,
    fontSize: '0.7rem',
    fontFamily: 'monospace',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  examplesList: {
    marginTop: theme.spacing(0.5),
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  docsLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
  },
  noSchema: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  statusIcon: {
    fontSize: 14,
    flexShrink: 0,
  },
  statusIconError: {
    color: theme.palette.error.main,
  },
  statusIconValid: {
    color: theme.palette.success.main,
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export interface SchemaReferencePanelProps {
  /** Current entity kind (e.g., "Component", "API") */
  kind?: string;
  /** Current validation errors */
  errors?: ValidationError[];
  /** Callback when a field is clicked - receives the field path */
  onFieldClick?: (field: FieldInfo) => void;
  /** Currently selected field path */
  selectedFieldPath?: string;
  /** Width of the panel */
  width?: number;
  /** Callback when close is clicked */
  onClose?: () => void;
  /** Current YAML content - used to determine which fields are in use */
  content?: string;
}

export const SchemaReferencePanel = ({
  kind,
  errors = [],
  onFieldClick,
  selectedFieldPath,
  width = PANEL_DEFAULT_WIDTH,
  onClose,
  content,
}: SchemaReferencePanelProps) => {
  const classes = useStyles();
  const yamlEditorApi = useApi(yamlEditorApiRef);
  
  const [schema, setSchema] = useState<KindSchema | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  
  // Resize state
  const [panelWidth, setPanelWidth] = useState(width);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(width);

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = panelWidth;
  }, [panelWidth]);

  // Handle resize move and end
  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.min(
        PANEL_MAX_WIDTH,
        Math.max(PANEL_MIN_WIDTH, resizeStartWidth.current + deltaX)
      );
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  // Fetch schema when kind changes
  useEffect(() => {
    if (!kind) {
      setSchema(null);
      return;
    }

    setLoading(true);
    yamlEditorApi.getKindSchema(kind)
      .then(setSchema)
      .catch(() => setSchema(null))
      .finally(() => setLoading(false));
  }, [kind, yamlEditorApi]);

  // Parse current YAML content to determine which fields are in use
  const fieldsInUse = useMemo(() => {
    const inUse = new Set<string>();
    if (!content) return inUse;

    try {
      const docs = YAML.parseAllDocuments(content);
      if (docs.length === 0) return inUse;
      
      const entity = docs[0].toJS();
      if (!entity || typeof entity !== 'object') return inUse;

      // Helper to recursively check for field presence
      const checkFields = (obj: Record<string, unknown>, prefix: string) => {
        if (!obj || typeof obj !== 'object') return;
        
        for (const [key, value] of Object.entries(obj)) {
          const path = prefix ? `${prefix}.${key}` : key;
          if (value !== undefined && value !== null) {
            inUse.add(path);
            // Check nested objects but not arrays
            if (typeof value === 'object' && !Array.isArray(value)) {
              checkFields(value as Record<string, unknown>, path);
            }
          }
        }
      };

      // Check metadata fields
      if (entity.metadata) {
        checkFields(entity.metadata, 'metadata');
      }

      // Check spec fields
      if (entity.spec) {
        checkFields(entity.spec, 'spec');
      }
    } catch {
      // Ignore parse errors
    }

    return inUse;
  }, [content]);

  // Create error map by field path
  const errorsByPath = useMemo(() => {
    const map = new Map<string, ValidationError[]>();
    for (const error of errors) {
      if (error.path) {
        const existing = map.get(error.path) || [];
        existing.push(error);
        map.set(error.path, existing);
      }
    }
    return map;
  }, [errors]);

  // Toggle field expansion
  const toggleField = useCallback((path: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  // Copy value to clipboard
  const handleCopy = useCallback((value: string) => {
    navigator.clipboard.writeText(value);
  }, []);

  // Render a field card
  const renderFieldCard = useCallback((field: FieldInfo) => {
    const fieldErrors = errorsByPath.get(field.path);
    const hasError = fieldErrors && fieldErrors.length > 0;
    const isExpanded = expandedFields.has(field.path);
    const isSelected = selectedFieldPath === field.path;
    const isInUse = fieldsInUse.has(field.path);

    return (
      <div
        key={field.path}
        className={`${classes.fieldCard} ${
          isSelected ? classes.fieldCardSelected : ''
        } ${hasError ? classes.fieldCardError : ''} ${
          isInUse && !hasError && !field.required ? classes.fieldCardValid : ''
        }`}
        onClick={() => {
          toggleField(field.path);
          onFieldClick?.(field);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleField(field.path);
            onFieldClick?.(field);
          }
        }}
      >
        <div className={classes.fieldHeader}>
          <Typography className={classes.fieldName}>{field.name}</Typography>
          <Chip size="small" label={field.type} className={classes.typeChip} variant="outlined" />
          {hasError && <ErrorOutlineIcon className={`${classes.statusIcon} ${classes.statusIconError}`} />}
          {!hasError && isInUse && <CheckCircleIcon className={`${classes.statusIcon} ${classes.statusIconValid}`} />}
          <IconButton size="small" style={{ marginLeft: 'auto', padding: 4, flexShrink: 0 }}>
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </div>

        <Typography className={classes.fieldDescription}>
          {field.description}
        </Typography>

        <Collapse in={isExpanded}>
          <div className={classes.fieldDetails}>
            {/* Path */}
            <Typography variant="caption" color="textSecondary" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <CodeIcon style={{ fontSize: 12 }} />
              <span style={{ fontFamily: 'monospace' }}>{field.path}</span>
            </Typography>

            {/* Error message if any */}
            {hasError && fieldErrors && (
              <Box mt={1} p={1} style={{ backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: 4 }}>
                <Typography variant="caption" color="error">
                  <strong>Error:</strong> {fieldErrors[0].message}
                </Typography>
              </Box>
            )}

            {/* Valid Values */}
            {field.validValues && field.validValues.length > 0 && (
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">
                  <strong>Valid values:</strong>
                </Typography>
                <div className={classes.validValuesList}>
                  {field.validValues.map((value, i) => (
                    <Tooltip key={i} title="Click to copy">
                      <Chip
                        size="small"
                        label={value}
                        className={classes.validValueChip}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(value);
                        }}
                      />
                    </Tooltip>
                  ))}
                </div>
              </Box>
            )}

            {/* Examples */}
            {field.examples && field.examples.length > 0 && (
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">
                  <strong>Examples:</strong>
                </Typography>
                <div className={classes.examplesList}>
                  {field.examples.map((example, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>• {example}</span>
                      <Tooltip title="Copy">
                        <IconButton
                          size="small"
                          style={{ padding: 2 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(example);
                          }}
                        >
                          <FileCopyIcon style={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              </Box>
            )}

            {/* Pattern */}
            {field.pattern && (
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">
                  <strong>Pattern:</strong> <code>{field.pattern}</code>
                </Typography>
              </Box>
            )}

            {/* Constraints */}
            {(field.minLength || field.maxLength) && (
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">
                  <strong>Length:</strong>{' '}
                  {field.minLength && `min ${field.minLength}`}
                  {field.minLength && field.maxLength && ' / '}
                  {field.maxLength && `max ${field.maxLength}`}
                </Typography>
              </Box>
            )}

            {/* Docs Link */}
            {field.docsUrl && (
              <Link
                href={field.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.docsLink}
                onClick={(e) => e.stopPropagation()}
              >
                <LinkIcon fontSize="small" />
                View Documentation
              </Link>
            )}
          </div>
        </Collapse>
      </div>
    );
  }, [classes, errorsByPath, expandedFields, selectedFieldPath, toggleField, onFieldClick, handleCopy, fieldsInUse]);

  if (!kind) {
    return (
      <div className={classes.panelContainer} style={{ width: panelWidth }}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */}
        <div
          className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ''}`}
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
        />
        <Paper className={classes.panel} elevation={0} square>
          <div className={classes.header}>
            <div className={classes.headerTitle}>
              <HelpIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2">Schema Reference</Typography>
            </div>
            {onClose && (
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div className={classes.noSchema}>
            <InfoIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.5 }} />
            <Typography variant="body2">
              Add a <code>kind</code> field to see the schema reference
            </Typography>
          </div>
        </Paper>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={classes.panelContainer} style={{ width: panelWidth }}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */}
        <div
          className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ''}`}
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
        />
        <Paper className={classes.panel} elevation={0} square>
          <div className={classes.header}>
            <Typography variant="subtitle2">Loading schema...</Typography>
            {onClose && (
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </Paper>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className={classes.panelContainer} style={{ width: panelWidth }}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */}
        <div
          className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ''}`}
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
        />
        <Paper className={classes.panel} elevation={0} square>
          <div className={classes.header}>
            <Typography variant="subtitle2">Schema: {kind}</Typography>
            {onClose && (
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div className={classes.noSchema}>
            <ErrorOutlineIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.5 }} />
            <Typography variant="body2">
              No schema found for kind "{kind}"
            </Typography>
          </div>
        </Paper>
      </div>
    );
  }

  const requiredFields = [
    ...schema.fields.root.filter(f => f.required),
    ...schema.fields.metadata.filter(f => f.required),
    ...schema.fields.spec.filter(f => f.required),
  ];
  
  // Sort optional fields: in-use fields first, then alphabetically by name
  const optionalFields = [
    ...schema.fields.root.filter(f => !f.required),
    ...schema.fields.metadata.filter(f => !f.required),
    ...schema.fields.spec.filter(f => !f.required),
  ].sort((a, b) => {
    const aInUse = fieldsInUse.has(a.path);
    const bInUse = fieldsInUse.has(b.path);
    if (aInUse && !bInUse) return -1;
    if (!aInUse && bInUse) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className={classes.panelContainer} style={{ width: panelWidth }}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ''}`}
        onMouseDown={handleResizeStart}
        role="separator"
        aria-orientation="vertical"
      />
      <Paper className={classes.panel} elevation={0} square>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <HelpIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2">{schema.kind} Schema</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {schema.docsUrl && (
              <Tooltip title="View full documentation">
                <IconButton
                  size="small"
                  component="a"
                  href={schema.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onClose && (
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label={`Required (${requiredFields.length})`} />
          <Tab label={`Optional (${optionalFields.length})`} />
        </Tabs>

        <div className={classes.content}>
          <Typography className={classes.kindDescription}>
            {schema.description}
          </Typography>

          <TabPanel value={activeTab} index={0}>
            {requiredFields.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No required fields
              </Typography>
            ) : (
              requiredFields.map(renderFieldCard)
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {optionalFields.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No optional fields
              </Typography>
            ) : (
              optionalFields.map(renderFieldCard)
            )}
          </TabPanel>
        </div>
      </Paper>
    </div>
  );
};

export default SchemaReferencePanel;

